import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";

/**
 * API functions for ReportsV2 store
 */
export function createReportsV2Api(surveyToken, currentReport, reports) {
  // API path helpers
  function getReportsPath() {
    return `/surveys/${surveyToken.value}/reports_v2`;
  }

  function getReportPath(reportToken = currentReport.value?.token) {
    return `${getReportsPath()}/${reportToken}`;
  }

  function getSlidesPath() {
    return `${getReportPath()}/slides`;
  }

  function getSlidePath(slideId) {
    return `${getSlidesPath()}/${slideId}`;
  }

  // Report CRUD operations
  async function fetchReports(loading) {
    loading.value = true;
    try {
      const { response, data } = await useMyFetch(getReportsPath()).get().json();

      if (response.value.ok) {
        reports.value = data.value;
      }
    } finally {
      loading.value = false;
    }
  }

  async function fetchReportData(reportToken) {
    const { response, data } = await useMyFetch(getReportPath(reportToken)).get().json();
    return { response, data };
  }

  async function createReportData(params) {
    const { response, data } = await useMyFetch(getReportsPath()).post({ reports_v2: params }).json();

    if (response.value.ok) captureEvent("report_created");

    return { response, data };
  }

  async function updateReportData(params) {
    const { response, data } = await useMyFetch(getReportPath()).patch({ reports_v2: params }).json();
    return { response, data };
  }

  async function deleteReportData(reportToken) {
    const { response } = await useMyFetch(getReportPath(reportToken)).delete();

    if (response.value.ok) captureEvent("report_deleted");

    return { response };
  }

  async function duplicateReportData(reportToken) {
    const { response, data } = await useMyFetch(`${getReportPath(reportToken)}/duplicate`).post().json();

    if (response.value.ok) captureEvent("report_duplicated");

    return { response, data };
  }

  // Slide API operations
  async function createSlideData(slideData) {
    const { response, data } = await useMyFetch(getSlidesPath())
      .post({ slide: slideData })
      .json();
    return { response, data };
  }

  async function updateSlideData(slideId, params) {
    const { response, data } = await useMyFetch(getSlidePath(slideId)).patch({ slide: params }).json();
    return { response, data };
  }

  async function deleteSlideData(slideId) {
    const { response } = await useMyFetch(getSlidePath(slideId)).delete();
    return { response };
  }

  async function reorderSlidesData(positions) {
    const { response, data } = await useMyFetch(`${getSlidesPath()}/reorder`).patch({ positions }).json();
    return { response, data };
  }

  // Generation API
  async function generateReportData(clientId) {
    const { response } = await useMyFetch(`${getReportPath()}/generate`).post({ client_id: clientId }).json();

    if (response.value.ok) {
      captureEvent("report_generated", { report_token: currentReport.value?.token });
    }

    return response.value.ok;
  }

  function getDownloadUrl() {
    return `/api${getReportPath()}/download`;
  }

  return {
    // Path helpers
    getReportsPath,
    getReportPath,
    getSlidesPath,
    getSlidePath,
    // Report operations
    fetchReports,
    fetchReportData,
    createReportData,
    updateReportData,
    deleteReportData,
    duplicateReportData,
    // Slide operations
    createSlideData,
    updateSlideData,
    deleteSlideData,
    reorderSlidesData,
    // Generation
    generateReportData,
    getDownloadUrl,
  };
}
